import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { User, Clock, CheckCircle, XCircle, University } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

interface PendingProfile {
  id: string;
  display_username: string | null;
  university: string | null;
  lab: string | null;
  city: string | null;
  country: string | null;
  created_at: string;
  approval_status: string;
  email?: string;
}

const PendingRegistrationsTable = () => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<PendingProfile | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  
  const { data: pendingUsers, isLoading, error } = useQuery({
    queryKey: ["pending-registrations"],
    queryFn: async () => {
      console.log('🔍 Fetching pending registrations...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          username,
          display_username,
          university,
          university_id,
          lab,
          lab_id,
          city,
          country,
          created_at,
          approval_status
        `)
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) {
        console.log('📋 No pending registrations found');
        return [] as PendingProfile[];
      }
      
      console.log(`📋 Found ${data.length} pending registrations`);
      
      // Get university and lab names for users with IDs
      const usersWithNames = await Promise.all(
        data.map(async (profile: any) => {
          let universityName = profile.university;
          let labName = profile.lab;
          
          // Fetch university name if we have an ID but no name
          if (profile.university_id && !profile.university) {
            try {
              const { data: uniData } = await supabase
                .from('universities')
                .select('name')
                .eq('id', profile.university_id)
                .single();
              universityName = uniData?.name || null;
            } catch (err) {
              console.error('Error fetching university:', err);
            }
          }
          
          // Fetch lab name if we have an ID but no name
          if (profile.lab_id && !profile.lab) {
            try {
              const { data: labData } = await supabase
                .from('labs')
                .select('name')
                .eq('id', profile.lab_id)
                .single();
              labName = labData?.name || null;
            } catch (err) {
              console.error('Error fetching lab:', err);
            }
          }
          
          return {
            ...profile,
            email: profile.username || 'No email', // username contains the email
            university: universityName,
            lab: labName
          };
        })
      );

      console.log('✅ Pending registrations processed with names');
      return usersWithNames as PendingProfile[];
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Always refetch on mount
  });

  const handleApproveUser = async (userId: string) => {
    console.log('🔄 Starting approval process for user:', userId);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          approval_status: 'approved',
          approved_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('❌ Database error during approval:', error);
        throw error;
      }

      console.log('✅ User approved in database:', data);
      toast.success("User approved successfully");

      // Aggressive cache invalidation and refresh
      console.log('🔄 Invalidating and refetching queries...');
      
      // First invalidate the cache
      queryClient.invalidateQueries({ queryKey: ["pending-registrations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
      
      // Then force a refetch with a small delay to ensure DB consistency
      setTimeout(async () => {
        await queryClient.refetchQueries({ queryKey: ["pending-registrations"] });
        await queryClient.refetchQueries({ queryKey: ["admin-profiles"] });
        console.log('🔄 Cache refresh completed');
      }, 100);

      // Send email notification (don't block on this)
      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            type: 'user_approved',
            userId,
            userEmail: selectedUser?.email
          }
        });
        console.log('📧 Approval email sent');
      } catch (emailError) {
        console.warn('⚠️ Email notification failed:', emailError);
      }

      setActionDialogOpen(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('💥 Error approving user:', error);
      toast.error(`Failed to approve user: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRejectUser = async (userId: string) => {
    console.log('🚫 Starting rejection process for user:', userId);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          approval_status: 'rejected'
        })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('❌ Database error during rejection:', error);
        throw error;
      }

      console.log('✅ User rejected in database:', data);
      toast.success("User registration rejected");

      // Aggressive cache invalidation and refresh
      console.log('🔄 Invalidating and refetching queries...');
      
      queryClient.invalidateQueries({ queryKey: ["pending-registrations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
      
      setTimeout(async () => {
        await queryClient.refetchQueries({ queryKey: ["pending-registrations"] });
        console.log('🔄 Cache refresh completed after rejection');
      }, 100);

      // Send email notification (don't block on this)
      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            type: 'user_rejected',
            userId,
            userEmail: selectedUser?.email
          }
        });
        console.log('📧 Rejection email sent');
      } catch (emailError) {
        console.warn('⚠️ Email notification failed:', emailError);
      }

      setActionDialogOpen(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('💥 Error rejecting user:', error);
      toast.error(`Failed to reject user: ${error.message || 'Unknown error'}`);
    }
  };

  const openActionDialog = (user: PendingProfile, action: 'approve' | 'reject') => {
    setSelectedUser(user);
    setActionType(action);
    setActionDialogOpen(true);
  };

  if (isLoading) {
    return <div className="p-4 text-center">Loading pending registrations...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-destructive">Error loading pending registrations: {error.toString()}</p>
      </div>
    );
  }

  if (!pendingUsers || pendingUsers.length === 0) {
    return (
      <div className="p-4 text-center">
        <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-muted-foreground">No pending registrations</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>University & Lab</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{user.display_username || 'No username'}</p>
                      <p className="text-xs text-muted-foreground">{user.id.substring(0, 8)}...</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <University className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm">{user.university || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{user.lab || 'N/A'}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {user.city && user.country ? (
                    <p className="text-sm">{`${user.city}, ${user.country}`}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">N/A</p>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Pending
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => openActionDialog(user, 'approve')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openActionDialog(user, 'reject')}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Registration' : 'Reject Registration'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {actionType} the registration for {selectedUser?.display_username || selectedUser?.email}?
              {actionType === 'approve' ? ' They will receive an email notification and be able to access the platform.' : ' They will be notified via email.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={actionType === 'approve' ? 'default' : 'destructive'}
              onClick={() => {
                if (selectedUser) {
                  if (actionType === 'approve') {
                    handleApproveUser(selectedUser.id);
                  } else {
                    handleRejectUser(selectedUser.id);
                  }
                }
              }}
            >
              {actionType === 'approve' ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve User
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Registration
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PendingRegistrationsTable;